<template>
  <AppLayout>
    <div class="flex flex-col min-h-full bg-slate-50 dark:bg-slate-950">

      <!-- ───────────────────────────────────────────
           MOBILE HEADER
      ─────────────────────────────────────────────── -->
      <div class="md:hidden -mx-0">
        <!-- Gradient hero -->
        <div class="bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-500 px-4 pt-3 pb-6">
          <div class="flex items-start justify-between mb-4">
            <div>
              <h1 class="text-xl font-bold text-white">Sales Report</h1>
              <p class="text-indigo-200 text-xs mt-0.5">{{ periodLabel }}</p>
            </div>
            <!-- Filters button -->
            <button @click="mobileFiltersOpen = true"
              class="relative flex items-center gap-1.5 bg-white/20 active:bg-white/30 text-white text-xs font-semibold px-3.5 py-2 rounded-xl transition">
              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75"/>
              </svg>
              Filters
              <span v-if="mobileFilterBadge > 0"
                class="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-white text-indigo-600 text-[10px] font-bold flex items-center justify-center shadow">
                {{ mobileFilterBadge }}
              </span>
            </button>
          </div>

          <!-- Hero stats (3 primary KPIs) -->
          <div v-if="!loading" class="grid grid-cols-3 gap-2">
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-emerald-200 uppercase tracking-widest mb-1">Sales</p>
              <p class="text-sm font-bold text-white whitespace-nowrap">{{ compactNum(kpis.total_sales) }}</p>
              <p class="text-[9px] text-indigo-200 mt-0.5">{{ kpis.sale_count || 0 }} inv</p>
            </div>
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-violet-200 uppercase tracking-widest mb-1">Profit</p>
              <p class="text-sm font-bold text-white whitespace-nowrap">{{ compactNum(kpis.gross_profit) }}</p>
              <p class="text-[9px] text-indigo-200 mt-0.5">gross</p>
            </div>
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-indigo-200 uppercase tracking-widest mb-1">Margin</p>
              <p class="text-sm font-bold text-white">{{ kpis.margin || 0 }}%</p>
              <p class="text-[9px] text-indigo-200 mt-0.5">of sales</p>
            </div>
          </div>
          <div v-else class="grid grid-cols-3 gap-2">
            <div v-for="i in 3" :key="i" class="bg-white/15 rounded-2xl h-14 animate-pulse"></div>
          </div>
        </div>

        <!-- Pull-up content card -->
        <div class="bg-gray-50 dark:bg-slate-950 rounded-t-3xl -mt-4 px-4 pt-4">

          <!-- Secondary KPI cards (2-col grid) -->
          <div v-if="!loading" class="grid grid-cols-2 gap-2 mb-4">
            <div v-for="kpi in secondaryKpiCards" :key="kpi.key"
              class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-3">
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">{{ kpi.label }}</p>
              <p class="text-sm font-bold" :class="kpi.color">{{ shortNum(kpi.value) }}</p>
              <p class="text-[10px] text-slate-400 mt-0.5">{{ kpi.sub }}</p>
            </div>
          </div>
          <div v-else class="grid grid-cols-2 gap-2 mb-4">
            <div v-for="i in 4" :key="i"
              class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-3 space-y-2">
              <div class="h-2 w-14 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
              <div class="h-4 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
              <div class="h-2 w-10 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            </div>
          </div>

          <!-- Chart -->
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4 mb-4">
            <div class="flex items-center justify-between mb-3">
              <p class="text-xs font-semibold text-slate-500 dark:text-slate-400">Performance</p>
              <div class="flex items-center gap-2">
                <!-- Dataset dropdown -->
                <div class="relative" data-ds-dropdown>
                  <button @click.stop="dsDropdownOpen = !dsDropdownOpen"
                    class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-[11px] font-semibold text-slate-600 dark:text-slate-300 transition">
                    <span class="flex items-center gap-0.5">
                      <span v-for="ds in datasetOptions" :key="ds.key" v-show="activeDatasets.includes(ds.key)"
                        class="w-1.5 h-1.5 rounded-full" :class="ds.dotClass"></span>
                    </span>
                    {{ activeDatasets.length < datasetOptions.length ? activeDatasets.length + ' selected' : 'All' }}
                    <svg class="w-3 h-3 text-slate-400 transition-transform" :class="dsDropdownOpen ? 'rotate-180' : ''"
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
                    </svg>
                  </button>
                  <div v-if="dsDropdownOpen"
                    class="absolute right-0 top-full mt-1.5 z-30 w-44 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-xl p-1.5 space-y-0.5">
                    <button v-for="ds in datasetOptions" :key="ds.key"
                      @click.stop="toggleDataset(ds.key)"
                      class="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold transition text-left"
                      :class="activeDatasets.includes(ds.key) ? ds.activeClass : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'">
                      <span class="w-3.5 h-3.5 rounded border-2 flex items-center justify-center shrink-0 transition"
                        :class="activeDatasets.includes(ds.key) ? 'border-current' : 'border-slate-300 dark:border-slate-600'">
                        <svg v-if="activeDatasets.includes(ds.key)" class="w-2 h-2" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 12 12">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M2 6l3 3 5-5"/>
                        </svg>
                      </span>
                      <span class="w-2 h-2 rounded-full shrink-0" :class="ds.dotClass"></span>
                      {{ ds.label }}
                    </button>
                  </div>
                </div>
                <!-- Bar / Line toggle -->
                <div class="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5 gap-0.5">
                  <button @click="chartType = 'line'"
                    class="px-2.5 py-1 rounded-md text-[11px] font-semibold transition"
                    :class="chartType === 'line' ? 'bg-white dark:bg-slate-700 text-slate-700 dark:text-white shadow-sm' : 'text-slate-400'">
                    Line
                  </button>
                  <button @click="chartType = 'bar'"
                    class="px-2.5 py-1 rounded-md text-[11px] font-semibold transition"
                    :class="chartType === 'bar' ? 'bg-white dark:bg-slate-700 text-slate-700 dark:text-white shadow-sm' : 'text-slate-400'">
                    Bar
                  </button>
                </div>
              </div>
            </div>
            <div v-if="loading" class="flex items-end gap-1.5 h-40">
              <div v-for="i in 8" :key="i" class="flex-1 bg-slate-100 dark:bg-slate-800 rounded animate-pulse"
                :style="{ height: (30 + Math.random() * 70) + '%' }"></div>
            </div>
            <BarChart v-else :key="chartType" :data="salesChartData" :options="chartOptions" style="max-height:180px" />
          </div>

          <!-- Breakdown: mobile cards -->
          <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Breakdown</p>
          <div v-if="loading" class="space-y-2 mb-6">
            <div v-for="i in 4" :key="i"
              class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4 space-y-2">
              <div class="h-3 w-24 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
              <div class="grid grid-cols-3 gap-2">
                <div v-for="j in 3" :key="j" class="h-10 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse"></div>
              </div>
            </div>
          </div>
          <div v-else-if="sortedTableData.length === 0"
            class="text-xs text-slate-400 text-center py-10 mb-6">No data for this period.</div>
          <div v-else class="space-y-2 mb-6">
            <div v-for="row in sortedTableData" :key="row.period"
              class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
              <!-- Period header -->
              <div class="flex items-center justify-between px-4 py-3 border-b border-slate-50 dark:border-slate-800">
                <span class="text-sm font-semibold text-slate-800 dark:text-white">{{ formatPeriod(row.period) }}</span>
                <div class="flex items-center gap-1.5">
                  <span class="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                    :class="Number(row.margin) >= 30
                      ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400'
                      : 'bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400'">
                    {{ row.margin }}%
                  </span>
                  <span v-if="momChange(row) !== null"
                    class="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                    :class="momChange(row) >= 0
                      ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400'
                      : 'bg-rose-50 dark:bg-rose-950 text-rose-600 dark:text-rose-400'">
                    {{ momChange(row) >= 0 ? '+' : '' }}{{ momChange(row) }}%
                  </span>
                </div>
              </div>
              <!-- Stats grid -->
              <div class="grid grid-cols-3 divide-x divide-slate-50 dark:divide-slate-800">
                <div class="px-3 py-2.5 text-center">
                  <p class="text-[9px] font-bold text-emerald-500 uppercase tracking-wide mb-0.5">Sales</p>
                  <p class="text-xs font-bold text-emerald-600 dark:text-emerald-400">{{ compactNum(row.sales) }}</p>
                </div>
                <div class="px-3 py-2.5 text-center">
                  <p class="text-[9px] font-bold text-indigo-500 uppercase tracking-wide mb-0.5">Purchases</p>
                  <p class="text-xs font-bold text-indigo-600 dark:text-indigo-400">{{ compactNum(row.purchases) }}</p>
                </div>
                <div class="px-3 py-2.5 text-center">
                  <p class="text-[9px] font-bold text-slate-400 uppercase tracking-wide mb-0.5">Profit</p>
                  <p class="text-xs font-bold"
                    :class="Number(row.profit) >= 0 ? 'text-slate-800 dark:text-white' : 'text-rose-600 dark:text-rose-400'">
                    {{ compactNum(row.profit) }}
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- ───────────────────────────────────────────
           DESKTOP HEADER + CONTENT
      ─────────────────────────────────────────────── -->
      <div class="hidden md:block p-6">

        <!-- Header row -->
        <div class="flex items-center justify-between mb-5">
          <div>
            <h1 class="text-2xl font-bold text-slate-800 dark:text-white">Sales Report</h1>
            <p class="text-slate-400 text-sm mt-0.5">{{ periodLabel }}</p>
          </div>

          <!-- Desktop filters -->
          <div class="flex items-center gap-2">
            <div class="relative">
              <select v-model="filters.preset"
                class="appearance-none bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                       text-slate-700 dark:text-slate-300 rounded-xl pl-3 pr-7 py-2 text-sm font-medium
                       focus:outline-none focus:ring-2 focus:ring-indigo-500 transition cursor-pointer">
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="this_year">This Year</option>
                <option value="last_year">Last Year</option>
                <option value="custom">Custom</option>
              </select>
              <svg class="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
              </svg>
            </div>
            <div class="relative">
              <select v-model="filters.groupBy"
                class="appearance-none bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                       text-slate-700 dark:text-slate-300 rounded-xl pl-3 pr-7 py-2 text-sm font-medium
                       focus:outline-none focus:ring-2 focus:ring-indigo-500 transition cursor-pointer">
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
              <svg class="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
              </svg>
            </div>
            <template v-if="filters.preset === 'custom'">
              <input type="date" v-model="filters.from"
                class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                       text-slate-700 dark:text-slate-300 rounded-xl px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-indigo-500 transition" />
              <span class="text-slate-400 text-sm">→</span>
              <input type="date" v-model="filters.to"
                class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                       text-slate-700 dark:text-slate-300 rounded-xl px-3 py-2 text-sm
                       focus:outline-none focus:ring-2 focus:ring-indigo-500 transition" />
            </template>
          </div>
        </div>

        <!-- KPI cards row -->
        <div class="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
          <template v-if="loading">
            <div v-for="i in 6" :key="i"
              class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-3 space-y-2">
              <div class="h-2.5 w-16 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
              <div class="h-4 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
              <div class="h-2 w-12 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            </div>
          </template>
          <template v-else>
            <div v-for="kpi in kpiCards" :key="kpi.key"
              class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-3">
              <div class="flex items-center justify-between mb-1">
                <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{{ kpi.label }}</p>
                <button
                  @mouseenter="showKpiTooltip($event, kpi.key)"
                  @mouseleave="kpiTooltip.visible = false"
                  class="text-slate-300 hover:text-slate-500 dark:text-slate-600 dark:hover:text-slate-400 transition shrink-0">
                  <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"/>
                  </svg>
                </button>
              </div>
              <p class="text-sm font-bold leading-tight" :class="kpi.color">{{ shortNum(kpi.value) }}</p>
              <p class="text-[10px] text-slate-400 mt-0.5">{{ kpi.sub }}</p>
            </div>
          </template>
        </div>

        <!-- KPI Tooltip -->
        <Teleport to="body">
          <div v-if="kpiTooltip.visible"
            class="fixed z-[9999] w-64 pointer-events-none"
            :style="{ top: kpiTooltip.y + 'px', left: kpiTooltip.x + 'px', transform: kpiTooltip.below ? 'translate(-50%, 0)' : 'translate(-50%, -100%)' }">
            <div v-if="kpiTooltip.below"
              class="w-2.5 h-2.5 bg-white dark:bg-slate-800 border-l border-t border-slate-200 dark:border-slate-700 rotate-45 mx-auto -mb-1.5"></div>
            <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl px-4 py-3 shadow-xl text-xs leading-relaxed">
              <p class="font-semibold text-slate-800 dark:text-white mb-1.5">{{ kpiTooltips[kpiTooltip.key]?.title }}</p>
              <p class="text-slate-500 dark:text-slate-300">{{ kpiTooltips[kpiTooltip.key]?.body }}</p>
              <p v-if="kpiTooltips[kpiTooltip.key]?.note" class="text-slate-400 dark:text-slate-400 mt-1.5 pt-1.5 border-t border-slate-100 dark:border-slate-700">
                {{ kpiTooltips[kpiTooltip.key]?.note }}
              </p>
            </div>
            <div v-if="!kpiTooltip.below"
              class="w-2.5 h-2.5 bg-white dark:bg-slate-800 border-r border-b border-slate-200 dark:border-slate-700 rotate-45 mx-auto -mt-1.5"></div>
          </div>
        </Teleport>

        <!-- Chart -->
        <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4 mb-5">
          <div class="flex items-center justify-between mb-3">
            <p class="text-sm font-semibold text-slate-700 dark:text-white">Performance</p>
            <div class="flex items-center gap-2">
              <!-- Dataset multi-select dropdown -->
              <div class="relative" data-ds-dropdown>
                <button @click.stop="dsDropdownOpen = !dsDropdownOpen"
                  class="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600 transition">
                  <span class="flex items-center gap-1">
                    <span v-for="ds in datasetOptions" :key="ds.key" v-show="activeDatasets.includes(ds.key)"
                      class="w-2 h-2 rounded-full" :class="ds.dotClass"></span>
                  </span>
                  {{ activeDatasets.length < datasetOptions.length ? activeDatasets.length + ' selected' : 'All datasets' }}
                  <svg class="w-3 h-3 text-slate-400 transition-transform" :class="dsDropdownOpen ? 'rotate-180' : ''"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
                  </svg>
                </button>
                <div v-if="dsDropdownOpen"
                  class="absolute right-0 top-full mt-1.5 z-30 w-48 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-xl p-1.5 space-y-0.5">
                  <button v-for="ds in datasetOptions" :key="ds.key"
                    @click.stop="toggleDataset(ds.key)"
                    class="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold transition text-left"
                    :class="activeDatasets.includes(ds.key) ? ds.activeClass : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'">
                    <span class="w-3.5 h-3.5 rounded border-2 flex items-center justify-center shrink-0 transition"
                      :class="activeDatasets.includes(ds.key) ? 'border-current' : 'border-slate-300 dark:border-slate-600'">
                      <svg v-if="activeDatasets.includes(ds.key)" class="w-2 h-2" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 12 12">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M2 6l3 3 5-5"/>
                      </svg>
                    </span>
                    <span class="w-2 h-2 rounded-full shrink-0" :class="ds.dotClass"></span>
                    {{ ds.label }}
                  </button>
                </div>
              </div>
              <!-- Bar / Line toggle -->
              <div class="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5 gap-0.5">
                <button @click="chartType = 'line'"
                  class="px-3 py-1 rounded-md text-xs font-semibold transition"
                  :class="chartType === 'line' ? 'bg-white dark:bg-slate-700 text-slate-700 dark:text-white shadow-sm' : 'text-slate-400'">
                  Line
                </button>
                <button @click="chartType = 'bar'"
                  class="px-3 py-1 rounded-md text-xs font-semibold transition"
                  :class="chartType === 'bar' ? 'bg-white dark:bg-slate-700 text-slate-700 dark:text-white shadow-sm' : 'text-slate-400'">
                  Bar
                </button>
              </div>
            </div>
          </div>
          <div v-if="loading" class="flex items-end gap-2 h-[220px]">
            <div v-for="i in 12" :key="i" class="flex-1 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse"
              :style="{ height: (30 + Math.random() * 70) + '%' }"></div>
          </div>
          <BarChart v-else :key="chartType" :data="salesChartData" :options="chartOptions" style="max-height:220px" />
        </div>

        <!-- Breakdown table -->
        <div>
          <p class="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Breakdown</p>
          <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
            <div v-if="loading" class="text-xs text-slate-400 text-center py-8">Loading...</div>
            <div v-else-if="salesTableData.length === 0" class="text-xs text-slate-400 text-center py-8">No data for this period.</div>
            <div v-else class="overflow-x-auto">
              <table class="w-full text-sm whitespace-nowrap">
                <thead>
                  <tr class="border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                    <th @click="toggleSort('period')"
                      class="text-left px-5 py-3 text-[10px] font-semibold uppercase tracking-wide cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-200"
                      :class="sortCol === 'period' ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'">
                      <span class="inline-flex items-center gap-1">Period <SortIcon :active="sortCol === 'period'" :asc="sortDir === 'asc'" /></span>
                    </th>
                    <th @click="toggleSort('sales')"
                      class="text-right px-5 py-3 text-[10px] font-semibold uppercase tracking-wide cursor-pointer select-none hover:text-emerald-600"
                      :class="sortCol === 'sales' ? 'text-emerald-600' : 'text-emerald-500'">
                      <span class="inline-flex items-center justify-end gap-1">Sales <SortIcon :active="sortCol === 'sales'" :asc="sortDir === 'asc'" /></span>
                    </th>
                    <th @click="toggleSort('purchases')"
                      class="text-right px-5 py-3 text-[10px] font-semibold uppercase tracking-wide cursor-pointer select-none hover:text-indigo-600"
                      :class="sortCol === 'purchases' ? 'text-indigo-600' : 'text-indigo-500'">
                      <span class="inline-flex items-center justify-end gap-1">Purchases <SortIcon :active="sortCol === 'purchases'" :asc="sortDir === 'asc'" /></span>
                    </th>
                    <th @click="toggleSort('profit')"
                      class="text-right px-5 py-3 text-[10px] font-semibold uppercase tracking-wide cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-200"
                      :class="sortCol === 'profit' ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'">
                      <span class="inline-flex items-center justify-end gap-1">Profit <SortIcon :active="sortCol === 'profit'" :asc="sortDir === 'asc'" /></span>
                    </th>
                    <th @click="toggleSort('margin')"
                      class="text-center px-5 py-3 text-[10px] font-semibold uppercase tracking-wide cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-200"
                      :class="sortCol === 'margin' ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'">
                      <span class="inline-flex items-center justify-center gap-1">Margin <SortIcon :active="sortCol === 'margin'" :asc="sortDir === 'asc'" /></span>
                    </th>
                    <th class="text-center px-5 py-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">vs Prev</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-50 dark:divide-slate-800">
                  <tr v-for="row in sortedTableData" :key="row.period"
                    class="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition">
                    <td class="px-5 py-3.5 font-semibold text-slate-800 dark:text-white">{{ formatPeriod(row.period) }}</td>
                    <td class="px-5 py-3.5 text-right font-semibold text-emerald-600 dark:text-emerald-400">{{ shortNum(row.sales) }}</td>
                    <td class="px-5 py-3.5 text-right font-semibold text-indigo-600 dark:text-indigo-400">{{ shortNum(row.purchases) }}</td>
                    <td class="px-5 py-3.5 text-right font-semibold"
                      :class="row.profit >= 0 ? 'text-slate-800 dark:text-white' : 'text-rose-600 dark:text-rose-400'">
                      {{ shortNum(row.profit) }}
                    </td>
                    <td class="px-5 py-3.5 text-center">
                      <span class="text-xs font-semibold px-2 py-0.5 rounded-full"
                        :class="row.margin >= 30
                          ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400'
                          : 'bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400'">
                        {{ row.margin }}%
                      </span>
                    </td>
                    <td class="px-5 py-3.5 text-center">
                      <span v-if="momChange(row) !== null"
                        class="text-xs font-semibold px-2 py-0.5 rounded-full"
                        :class="momChange(row) >= 0
                          ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400'
                          : 'bg-rose-50 dark:bg-rose-950 text-rose-600 dark:text-rose-400'">
                        {{ momChange(row) >= 0 ? '+' : '' }}{{ momChange(row) }}%
                      </span>
                      <span v-else class="text-xs text-slate-300 dark:text-slate-600">—</span>
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="border-t-2 border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                    <td class="px-5 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total</td>
                    <td class="px-5 py-3 text-right text-sm font-bold text-emerald-600 dark:text-emerald-400">{{ shortNum(kpis.total_sales) }}</td>
                    <td class="px-5 py-3 text-right text-sm font-bold text-indigo-600 dark:text-indigo-400">{{ shortNum(kpis.total_purchases) }}</td>
                    <td class="px-5 py-3 text-right text-sm font-bold"
                      :class="(kpis.gross_profit || 0) >= 0 ? 'text-slate-800 dark:text-white' : 'text-rose-600 dark:text-rose-400'">
                      {{ shortNum(kpis.gross_profit) }}
                    </td>
                    <td class="px-5 py-3 text-center">
                      <span class="text-xs font-bold px-2 py-0.5 rounded-full"
                        :class="(kpis.margin || 0) >= 30
                          ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400'
                          : 'bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400'">
                        {{ kpis.margin || 0 }}%
                      </span>
                    </td>
                    <td class="px-5 py-3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

      </div>

    </div>
    <!-- ── MOBILE FILTERS SHEET ── -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0" enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-200"
        leave-from-class="opacity-100" leave-to-class="opacity-0">
        <div v-if="mobileFiltersOpen"
          class="md:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          @click="mobileFiltersOpen = false" />
      </Transition>
      <Transition
        enter-active-class="transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
        enter-from-class="translate-y-full" enter-to-class="translate-y-0"
        leave-active-class="transition-transform duration-200 ease-in"
        leave-from-class="translate-y-0" leave-to-class="translate-y-full">
        <div v-if="mobileFiltersOpen"
          class="md:hidden fixed inset-x-0 bottom-0 z-50 bg-white dark:bg-slate-900 rounded-t-3xl shadow-2xl flex flex-col"
          style="max-height: 82vh; padding-bottom: env(safe-area-inset-bottom, 0px)"
          @click.stop>
          <!-- Handle -->
          <div class="flex justify-center pt-3 pb-1 shrink-0">
            <div class="w-10 h-1 rounded-full bg-slate-300 dark:bg-slate-600"></div>
          </div>
          <!-- Header -->
          <div class="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <p class="font-semibold text-slate-800 dark:text-white text-base">Filters</p>
            <div class="flex items-center gap-2">
              <button v-if="mobileFilterBadge > 0"
                @click="filters.preset = 'this_month'; filters.groupBy = 'daily'; filters.from = ''; filters.to = ''; mobileFiltersOpen = false"
                class="text-xs font-semibold text-red-500 px-3 py-1.5 rounded-full bg-red-50 dark:bg-red-950/50 transition">
                Reset
              </button>
              <button @click="mobileFiltersOpen = false"
                class="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>
          <!-- Content -->
          <div class="overflow-y-auto flex-1 px-4 py-5 space-y-6">

            <!-- Period -->
            <div>
              <p class="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">Period</p>
              <div class="flex flex-wrap gap-2">
                <button v-for="opt in presetOptions" :key="opt.v"
                  @click="filters.preset = opt.v"
                  class="text-sm font-semibold px-4 py-2 rounded-full transition active:scale-95"
                  :class="filters.preset === opt.v
                    ? 'bg-indigo-500 text-white shadow-sm'
                    : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700'">
                  {{ opt.l }}
                </button>
              </div>
              <!-- Custom date range -->
              <div v-if="filters.preset === 'custom'" class="flex items-center gap-2 mt-3">
                <input type="date" v-model="filters.from"
                  class="flex-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                         text-slate-700 dark:text-slate-300 text-sm rounded-xl px-3 py-2.5
                         focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                <span class="text-slate-400 text-sm">→</span>
                <input type="date" v-model="filters.to"
                  class="flex-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                         text-slate-700 dark:text-slate-300 text-sm rounded-xl px-3 py-2.5
                         focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
            </div>

            <!-- Group By -->
            <div>
              <p class="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">Group By</p>
              <div class="flex flex-wrap gap-2">
                <button v-for="opt in groupByOptions" :key="opt.v"
                  @click="filters.groupBy = opt.v"
                  class="text-sm font-semibold px-4 py-2 rounded-full transition active:scale-95"
                  :class="filters.groupBy === opt.v
                    ? 'bg-indigo-500 text-white shadow-sm'
                    : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700'">
                  {{ opt.l }}
                </button>
              </div>
            </div>

          </div>
          <!-- Apply button -->
          <div class="px-4 py-4 border-t border-slate-100 dark:border-slate-800 shrink-0">
            <button @click="mobileFiltersOpen = false"
              class="w-full py-3 rounded-2xl bg-indigo-500 hover:bg-indigo-400 active:scale-[0.98] text-white text-sm font-semibold transition shadow-sm shadow-indigo-500/30">
              Apply Filters
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>

  </AppLayout>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { Bar as BarChart } from 'vue-chartjs'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, Filler } from 'chart.js'
import AppLayout from '@/layouts/AppLayout.vue'
import SortIcon from '@/components/SortIcon.vue'

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, Filler)

const page = usePage()
const businessName = computed(() => page.props.auth?.user?.business_name?.trim() || 'Master Ledger')
onMounted(() => {
  document.title = `Sales Report | ${businessName.value}`
  fetchData()
  document.addEventListener('click', onDocClick)
})
onUnmounted(() => document.removeEventListener('click', onDocClick))

const loading = ref(true)
const kpis       = ref({})
const chart_data = ref([])

const mobileFiltersOpen = ref(false)

const presetOptions  = [
  { v: 'this_month', l: 'This Month' },
  { v: 'last_month', l: 'Last Month' },
  { v: 'this_year',  l: 'This Year'  },
  { v: 'last_year',  l: 'Last Year'  },
  { v: 'custom',     l: 'Custom'     },
]
const groupByOptions = [
  { v: 'daily',   l: 'Daily'   },
  { v: 'weekly',  l: 'Weekly'  },
  { v: 'monthly', l: 'Monthly' },
]

const mobileFilterBadge = computed(() => {
  let count = 0
  if (filters.value.preset  !== 'this_month') count++
  if (filters.value.groupBy !== 'daily')       count++
  return count
})

const filters = ref({
  preset:  'this_month',
  groupBy: 'daily',
  from:    '',
  to:      '',
})

const chartType      = ref('line')
const activeDatasets = ref(['sale', 'purchase', 'profit', 'margin'])

const datasetOptions = [
  { key: 'sale',     label: 'Sale',     dotClass: 'bg-emerald-500', activeClass: 'bg-emerald-50 dark:bg-emerald-950/60 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400' },
  { key: 'purchase', label: 'Purchase', dotClass: 'bg-indigo-500',  activeClass: 'bg-indigo-50 dark:bg-indigo-950/60 border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-400' },
  { key: 'profit',   label: 'Profit',   dotClass: 'bg-amber-400',   activeClass: 'bg-amber-50 dark:bg-amber-950/60 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400' },
  { key: 'margin',   label: 'Margin',   dotClass: 'bg-violet-400',  activeClass: 'bg-violet-50 dark:bg-violet-950/60 border-violet-300 dark:border-violet-700 text-violet-700 dark:text-violet-400' },
]

function toggleDataset(key) {
  const idx = activeDatasets.value.indexOf(key)
  if (idx === -1) {
    activeDatasets.value.push(key)
  } else if (activeDatasets.value.length > 1) {
    activeDatasets.value.splice(idx, 1)
  }
}

const dsDropdownOpen = ref(false)
function onDocClick(e) {
  if (!e.target.closest('[data-ds-dropdown]')) dsDropdownOpen.value = false
}

async function fetchData() {
  loading.value = true
  const params = new URLSearchParams({
    preset:   filters.value.preset,
    group_by: filters.value.groupBy,
  })
  if (filters.value.preset === 'custom') {
    if (filters.value.from) params.set('from', filters.value.from)
    if (filters.value.to)   params.set('to',   filters.value.to)
  }
  try {
    const res  = await fetch(`/reports/sales/data?${params}`, {
      headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    })
    const data = await res.json()
    kpis.value       = data.kpis       || {}
    chart_data.value = data.chart_data || []
  } finally {
    loading.value = false
  }
}

watch(filters, (val) => {
  if (val.preset === 'custom' && (!val.from || !val.to)) return
  fetchData()
}, { deep: true })

const periodLabel = computed(() => {
  const map = { this_month: 'This Month', last_month: 'Last Month', this_year: 'This Year', last_year: 'Last Year', custom: 'Custom Range' }
  return map[filters.value.preset] || 'Custom Range'
})

function shortNum(n) {
  n = Number(n) || 0
  return 'Rs ' + Math.floor(n).toLocaleString('en-PK')
}

function compactNum(n) {
  n = Number(n) || 0
  return 'Rs ' + Math.floor(n).toLocaleString('en-PK')
}

function formatPeriod(period) {
  if (!period) return ''
  const d = new Date(period)
  if (filters.value.groupBy === 'daily')  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  if (filters.value.groupBy === 'weekly') return 'Week of ' + d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
}

// ── KPI cards ──────────────────────────────────────────────────────────────────
const kpiCards = computed(() => [
  { key: 'total_sales',        label: 'Total Sales',    value: kpis.value?.total_sales,        color: 'text-emerald-600 dark:text-emerald-400', sub: `${kpis.value?.sale_count || 0} invoices` },
  { key: 'total_purchases',    label: 'Purchases',      value: kpis.value?.total_purchases,     color: 'text-indigo-600 dark:text-indigo-400',   sub: `${kpis.value?.purchase_count || 0} invoices` },
  { key: 'gross_profit',       label: 'Gross Profit',   value: kpis.value?.gross_profit,        color: (kpis.value?.gross_profit || 0) >= 0 ? 'text-slate-800 dark:text-white' : 'text-rose-600 dark:text-rose-400', sub: `${kpis.value?.margin || 0}% margin` },
  { key: 'total_cost',         label: 'Total Cost',     value: kpis.value?.total_cost,          color: 'text-rose-600 dark:text-rose-400',       sub: 'COGS' },
  { key: 'avg_sale_value',     label: 'Avg Sale',       value: kpis.value?.avg_sale_value,      color: 'text-emerald-600 dark:text-emerald-400', sub: 'per invoice' },
  { key: 'avg_purchase_value', label: 'Avg Purchase',   value: kpis.value?.avg_purchase_value,  color: 'text-indigo-600 dark:text-indigo-400',   sub: 'per invoice' },
])

// Secondary KPIs for mobile (below the hero)
const secondaryKpiCards = computed(() => [
  { key: 'total_purchases',    label: 'Purchases',    value: kpis.value?.total_purchases,    color: 'text-indigo-600 dark:text-indigo-400',   sub: `${kpis.value?.purchase_count || 0} invoices` },
  { key: 'total_cost',         label: 'Total Cost',   value: kpis.value?.total_cost,         color: 'text-rose-600 dark:text-rose-400',       sub: 'COGS' },
  { key: 'avg_sale_value',     label: 'Avg Sale',     value: kpis.value?.avg_sale_value,     color: 'text-emerald-600 dark:text-emerald-400', sub: 'per invoice' },
  { key: 'avg_purchase_value', label: 'Avg Purchase', value: kpis.value?.avg_purchase_value, color: 'text-indigo-600 dark:text-indigo-400',   sub: 'per invoice' },
])

const kpiTooltips = {
  total_sales:        { title: 'Total Sales',        body: 'SUM(quantity × rate) across all sold invoices within the selected date range.' },
  total_purchases:    { title: 'Purchases',          body: 'SUM(quantity × rate) across all purchased invoices within the selected date range.' },
  gross_profit:       { title: 'Gross Profit',       body: 'Total Sales − Total Cost (COGS).', note: 'Margin = Gross Profit ÷ Total Sales × 100' },
  total_cost:         { title: 'Total Cost (COGS)',  body: 'SUM(quantity × purchase_rate) on sold invoices — the cost of goods actually sold, not what was purchased.' },
  avg_sale_value:     { title: 'Avg Sale Value',     body: 'Total Sales ÷ number of distinct sale invoices in the period.' },
  avg_purchase_value: { title: 'Avg Purchase Value', body: 'Total Purchases ÷ number of distinct purchase invoices in the period.' },
}

const kpiTooltip = ref({ visible: false, key: null, x: 0, y: 0, below: false })

function showKpiTooltip(event, key) {
  const rect = event.target.getBoundingClientRect()
  const tooltipHeight = 110
  const spaceAbove = rect.top
  const below = spaceAbove < tooltipHeight + 16
  kpiTooltip.value = {
    visible: true,
    key,
    x: Math.min(rect.left + rect.width / 2 + window.scrollX, window.innerWidth - 140),
    y: below ? rect.bottom + window.scrollY + 8 : rect.top + window.scrollY - 8,
    below,
  }
}

const salesTableData = computed(() => [...chart_data.value].reverse())

const sortCol = ref('period')
const sortDir = ref('desc')

function toggleSort(col) {
  if (sortCol.value === col) {
    sortDir.value = sortDir.value === 'desc' ? 'asc' : 'desc'
  } else {
    sortCol.value = col
    sortDir.value = 'desc'
  }
}

const sortedTableData = computed(() => {
  const arr = [...salesTableData.value]
  const asc = sortDir.value === 'asc'
  return arr.sort((a, b) => {
    let av, bv
    if (sortCol.value === 'period')         { av = a.period;            bv = b.period }
    else if (sortCol.value === 'sales')     { av = Number(a.sales);     bv = Number(b.sales) }
    else if (sortCol.value === 'purchases') { av = Number(a.purchases); bv = Number(b.purchases) }
    else if (sortCol.value === 'profit')    { av = Number(a.profit);    bv = Number(b.profit) }
    else if (sortCol.value === 'margin')    { av = Number(a.margin);    bv = Number(b.margin) }
    if (av < bv) return asc ? -1 : 1
    if (av > bv) return asc ? 1 : -1
    return 0
  })
})

function momChange(row) {
  const arr = salesTableData.value
  const idx = arr.findIndex(r => r.period === row.period)
  if (idx >= arr.length - 1) return null
  const curr = Number(arr[idx].sales)
  const prev = Number(arr[idx + 1].sales)
  if (prev === 0) return null
  return ((curr - prev) / prev * 100).toFixed(1)
}

const tickColor = '#94a3b8'
const gridColor = 'rgba(148,163,184,0.12)'
const baseFont  = { family: 'inherit', size: 10 }

const chartOptions = computed(() => ({
  responsive: true, maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: { mode: 'index' },
  },
  scales: {
    x: { grid: { display: false }, ticks: { color: tickColor, font: baseFont } },
    y: { grid: { color: gridColor }, ticks: { color: tickColor, font: baseFont, callback: v => (v / 1000) + 'k' } },
    yRight: {
      display: activeDatasets.value.includes('margin'),
      position: 'right',
      grid: { display: false },
      ticks: { color: 'rgba(167,139,250,0.9)', font: baseFont, callback: v => v + '%' },
    },
  },
}))

const salesChartData = computed(() => {
  const isBar = chartType.value === 'bar'
  const active = activeDatasets.value
  const datasets = []

  if (active.includes('sale')) {
    datasets.push({
      type: isBar ? 'bar' : 'line',
      label: 'Sales',
      data: chart_data.value.map(r => r.sales),
      backgroundColor: isBar ? 'rgba(16,185,129,0.85)' : 'rgba(16,185,129,0.1)',
      borderColor: 'rgba(16,185,129,1)',
      borderWidth: isBar ? 0 : 2,
      borderRadius: isBar ? 3 : 0,
      pointRadius: isBar ? 0 : 3,
      tension: 0.35,
      fill: !isBar,
      yAxisID: 'y',
    })
  }

  if (active.includes('purchase')) {
    datasets.push({
      type: isBar ? 'bar' : 'line',
      label: 'Purchases',
      data: chart_data.value.map(r => r.purchases),
      backgroundColor: isBar ? 'rgba(99,102,241,0.85)' : 'rgba(99,102,241,0.1)',
      borderColor: 'rgba(99,102,241,1)',
      borderWidth: isBar ? 0 : 2,
      borderRadius: isBar ? 3 : 0,
      pointRadius: isBar ? 0 : 3,
      tension: 0.35,
      fill: !isBar,
      yAxisID: 'y',
    })
  }

  if (active.includes('profit')) {
    datasets.push({
      type: 'line',
      label: 'Profit',
      data: chart_data.value.map(r => r.profit),
      borderColor: 'rgba(251,191,36,1)',
      backgroundColor: 'rgba(251,191,36,0.08)',
      borderWidth: 2,
      pointRadius: 3,
      tension: 0.35,
      fill: false,
      yAxisID: 'y',
    })
  }

  if (active.includes('margin')) {
    datasets.push({
      type: 'line',
      label: 'Margin %',
      data: chart_data.value.map(r => r.margin),
      borderColor: 'rgba(167,139,250,1)',
      backgroundColor: 'rgba(167,139,250,0.08)',
      borderWidth: 2,
      borderDash: [5, 3],
      pointRadius: 3,
      tension: 0.35,
      fill: false,
      yAxisID: 'yRight',
    })
  }

  return {
    labels: chart_data.value.map(r => formatPeriod(r.period)),
    datasets,
  }
})
</script>

<style scoped>
.scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
.scrollbar-hide::-webkit-scrollbar { display: none; }
</style>
